import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { replaceCaseInsensitive } from '../../../../Assembler/dist/common/commonUtil.js';
import { JsonArray, JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { ResourceStatistics, InheritanceOperation, NavigationDecision, TemplateOperation } from '../../../../Assembler/dist/model/index.js';
import { TemplateOperationKind } from '../../../../Assembler/dist/model/index.js';
import { TemplateStoreJson } from '../../model/index.js';
import { DynamicResultsJson } from '../../model/dynamicResultsJson.js';
import { ILoaderNormalJson } from './iLoaderNormalJson.js';
import type { GetTemplateJsonResult } from './iLoaderNormalJson.js';
import type { IConfig, AppFileResult } from '../../../../Assembler/dist/config/iConfig.js';
import type { DynamicConfigMap } from '../../../../Assembler/dist/registry/dynamicConfigMap.js';
import { TransientStore } from '../../../../Assembler/dist/registry/transientStore.js';
import type { ITransientStore } from '../../../../Assembler/dist/registry/iface/iTransientStore.js';
import type { AppSiteResult } from '../../../../Assembler/dist/registry/iface/appSiteResult.js';

// Import from split modules
import { inheritanceMethods } from './loaderNormalJson_Inheritance.js';

import {
    dynamicMethods
} from './loaderNormalJson_Dynamic.js';
import { apiMethods } from './loaderNormalJson_Api.js';

// Import method objects from split files (matching C# partial class structure)
import { accessorMethods, accessorStaticMethods } from './loaderNormalJson_Accessors.js';
import { iloaderMethods } from './loaderNormalJson_ILoader.js';
import { lifecycleMethods } from './loaderNormalJson_Lifecycle.js';

type PlainJsonObject = { [key: string]: unknown };
type PlainJsonMap = globalThis.Map <string, unknown>;

/**
 * Cache entry type for htmlJsonCache values (HTML template structures)
 */
export interface JsonCacheEntry {
    relativePath: string;
    jsonStructure: JsonObject;
}

/**
 * Cache entry type for dataJsonCache values (JSON data structures)
 */
export interface DataJsonCacheEntry {
    relativePath: string;
    jsonStructure: JsonObject;
}

/**
 * Loader that implements ILoaderNormalJson for Normal engine
 * Generates HtmlJson and DataJson without metadata
 */
export class LoaderNormalJson extends ILoaderNormalJson {
    // #region Private Variables

    // Instance-level store - separates loaded data from per-merge mutable state
    _store: TemplateStoreJson = new TemplateStoreJson();

    // Backward-compatible accessors that delegate to the store
    get _htmlJsonCache(): Map<string, Map<string, JsonCacheEntry>> {
        return (this._store as unknown as { _htmlJsonCache: Map<string, Map<string, JsonCacheEntry>> })._htmlJsonCache;
    }
    get _dataJsonCache(): Map<string, Map<string, DataJsonCacheEntry>> {
        return (this._store as unknown as { _dataJsonCache: Map<string, Map<string, DataJsonCacheEntry>> })._dataJsonCache;
    }
    get _resourceStatsCache(): Map<string, ResourceStatistics> {
        return (this._store as unknown as { _resourceStatsCache: Map<string, ResourceStatistics> })._resourceStatsCache;
    }

    _config: IConfig | null = null;
    _appSite: string = '';

    // Track Inheritance operations for flow data
    _inheritanceOperations: InheritanceOperation[] = [];

    // Per-merge mutable state for dynamic handler results
    _dynamicResultsJson: DynamicResultsJson = new DynamicResultsJson();
    _transientStore: ITransientStore = new TransientStore();

    // DynamicData timing instrumentation - in nanoseconds
    _dynamicDataNanos: number = 0;

    // DynamicView timing instrumentation - in nanoseconds
    _dynamicViewNanos: number = 0;
    _dynamicViewCount: number = 0;
    _dynamicDataCount: number = 0;

    // DynamicClientData timing instrumentation - in nanoseconds (F4)
    _dynamicClientDataNanos: number = 0;
    _dynamicClientDataCount: number = 0;

    // Skip patterns for DynamicView/DynamicData handlers
    _skipDynamicView: string | null = null;
    _skipDynamicData: string | null = null;

    _dynamicActionNanos: number = 0;
    _dynamicActionCount: number = 0;
    _actionMode: boolean = false;

    // Pre-loaded stores (populated during Load, used during engine processing)
    _dynamicDataConfigStore: Record<string, DynamicConfigMap | null> = {};
    _dynamicClientDataConfigStore: Record<string, DynamicConfigMap | null> = {};
    _dynamicViewConfigStore: Record<string, DynamicConfigMap | null> = {};
    _dynamicActionConfigStore: Record<string, DynamicConfigMap | null> = {};
    _dynamicAppSiteConfigStore: Record<string, DynamicConfigMap | null> = {};
    _dynamicDataParamsStore: JsonObject = new JsonObject();
    _dynamicClientDataParamsStore: JsonObject = new JsonObject();
    _dynamicViewParamsStore: JsonObject = new JsonObject();
    _dynamicActionParamsStore: JsonObject = new JsonObject();
    _dynamicAppSiteParamsStore: JsonObject = new JsonObject();

    // Per-merge request context (stashed at merge top from applyDynamicAppSiteHandlers). Reserved for
    // the AppSite/authorization phase (later phase). Mirrors the C# loader request context.
    _requestContextJson: JsonObject | null = null;
    // Per-merge navigation trace (accumulated by applyDynamicAppSiteHandlers; drained by the engine into
    // MergeResult.navigationTrace via getAndClearNavigationTrace). Mirrors _inheritanceOperations ownership.
    _lastNavigationTrace: NavigationDecision[] = [];
    _globalDataStore: Record<string, JsonObject | null> = {};
    _globalViewDataStore: Record<string, JsonObject | null> = {};
    _globalViewDataComponentStore: Record<string, JsonObject | null> = {};

    // Client-mode fields (set externally for client-side rendering)
    _globalData: Map<string, JsonObject> | null = null;
    _globalViewData: Map<string, JsonObject> | null = null;

    // #endregion

    // #region Mixin method declarations (attached via Object.assign to prototype)

    // --- From loaderNormalJson_ILoader.ts (iloaderMethods) ---
    declare load: (this: LoaderNormalJson, config: IConfig, appSite: string) => boolean;
    declare clearCache: (this: LoaderNormalJson) => void;
    declare getBaseComponentName: (this: LoaderNormalJson, placeholderOrSlotholderName: string, currentTemplateName: string) => { baseComponentName: string; isReuseComponent: boolean };
    declare getDynamicDataConfig: (this: LoaderNormalJson, appSite: string) => DynamicConfigMap | null;
    declare setDynamicResults: (this: LoaderNormalJson, dynamicResults: DynamicResultsJson) => void;
    declare getDynamicDataTiming: (this: LoaderNormalJson) => { nanos: number; count: number };
    declare getDynamicViewTiming: (this: LoaderNormalJson) => { nanos: number; count: number };
    declare getDynamicClientDataTiming: (this: LoaderNormalJson) => { nanos: number; count: number };
    declare getDynamicActionConfig: (this: LoaderNormalJson, appSite: string) => DynamicConfigMap | null;
    declare getDynamicDataParams: (this: LoaderNormalJson, appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicClientDataConfig: (this: LoaderNormalJson, appSite: string) => DynamicConfigMap | null;
    declare getDynamicClientDataParams: (this: LoaderNormalJson, appSite: string, paramFile: string) => JsonObject | null;
    declare tryResolvePureClientHandlerParams: (this: LoaderNormalJson, appSite: string, componentName: string, clientHandlerName: string) => JsonObject | null;
    declare getDynamicViewConfig: (this: LoaderNormalJson, appSite: string) => DynamicConfigMap | null;
    declare getDynamicViewParams: (this: LoaderNormalJson, appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicActionParams: (this: LoaderNormalJson, appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicAppSiteConfig: (this: LoaderNormalJson, appSite: string) => DynamicConfigMap | null;
    declare getDynamicAppSiteParams: (this: LoaderNormalJson, appSite: string, paramFile: string) => JsonObject | null;
    declare ensureAppSiteLoaded: (this: LoaderNormalJson, appSite: string) => boolean;
    declare applyDynamicAppSiteHandlers: (this: LoaderNormalJson, appSite: string, appView: string | null, contextJson: JsonObject, configJson: JsonObject | null) => AppSiteResult;
    declare getGlobalData: (this: LoaderNormalJson, appSite: string) => JsonObject | null;
    declare getGlobalViewData: (this: LoaderNormalJson, appSite: string) => JsonObject | null;
    declare getGlobalViewDataForComponent: (this: LoaderNormalJson, appSite: string, componentName: string) => JsonObject | null;
    declare getDynamicActionTiming: (this: LoaderNormalJson) => { nanos: number; count: number };
    declare setActionMode: (this: LoaderNormalJson, actionMode: boolean) => void;
    declare getActionMode: (this: LoaderNormalJson) => boolean;
    declare getAndClearInheritanceOperations: (this: LoaderNormalJson) => InheritanceOperation[];
    declare getAndClearNavigationTrace: (this: LoaderNormalJson) => NavigationDecision[];
    declare getAppFile: (this: LoaderNormalJson, appSite: string, appView: string) => AppFileResult;
    declare _preloadDynamicAndGlobalData: (this: LoaderNormalJson, config: IConfig, appSite: string) => void;
    declare getTemplatesApiResponse: (this: LoaderNormalJson, appSite: string, appView: string, contextJson: JsonObject, serverTimeMs: number, includeSearchAppSites?: boolean, maskHtml?: boolean, orderedTemplateNames?: string[] | null) => string;
    declare setTransientStore: (this: LoaderNormalJson, transientStore: ITransientStore) => void;
    declare getTransientStore: (this: LoaderNormalJson) => ITransientStore;
    declare serializeDynamicConfigToJson: (this: LoaderNormalJson, config: DynamicConfigMap) => string;
    declare buildTemplatesJsonForAppSite: (this: LoaderNormalJson, htmlJsonCache: Map<string, Map<string, JsonCacheEntry>>, dataJsonCache: Map<string, Map<string, DataJsonCacheEntry>>, appSite: string, maskHtml?: boolean, orderedTemplateNames?: string[] | null) => string;
    declare _serializePerSiteStores: (this: LoaderNormalJson, sb: string[], appSite: string) => void;
    declare _serializePerSiteDynamicConfig: (this: LoaderNormalJson, sb: string[], allSites: string[], key: string, getter: (site: string) => DynamicConfigMap | null) => void;
    declare _serializePerSiteParamsFromStore: (this: LoaderNormalJson, sb: string[], allSites: string[], key: string, paramsStore: JsonObject) => void;
    declare getComponentId: (this: LoaderNormalJson, appSite: string, componentName: string) => string | null;

    // --- From loaderNormalJson_Accessors.ts (accessorMethods) ---
    declare hasTemplate: (this: LoaderNormalJson, appSite: string, currentTemplateName: string, appFileOrPlaceholderOrSlotholderName: string, appView?: string | null) => boolean;
    declare getSearchAppSitesForAppSite: (this: LoaderNormalJson, appSite: string) => string;
    declare getResourceStatistics: (this: LoaderNormalJson, appSite: string) => ResourceStatistics;
    declare _computeResourceStatistics: (this: LoaderNormalJson, appSite: string) => ResourceStatistics;
    declare _loadTemplatesFromSingleAppSite: (this: LoaderNormalJson, config: IConfig, appSite: string) => void;
    declare _getTemplateDataJsonWithInheritance: (this: LoaderNormalJson, appSite: string, appView: string, appFile: string, dataJson: JsonObject, resolvedTemplateName: string, flowStack?: string[] | null, resolvedAppSite?: string | null, configJson?: JsonObject | null, htmlJson?: JsonObject | null, clientMode?: boolean) => JsonObject | null;
    declare shouldSkipHandler: (this: LoaderNormalJson, handlerType: string, skipPatterns: string | null) => boolean;
    declare getLoaderStateJson: (this: LoaderNormalJson, appSite: string) => string;
    declare getLoaderPerSiteStoresJson: (this: LoaderNormalJson, appSite: string) => string;

    // --- From loaderNormalJson_Inheritance.ts (inheritanceMethods) ---
    declare hasDynamicDataHandlers: (this: LoaderNormalJson, appSite: string, componentName: string) => boolean;
    declare hasDynamicActionHandlers: (this: LoaderNormalJson, appSite: string, componentName: string) => boolean;
    declare hasGlobalData: (this: LoaderNormalJson, appSite: string, componentName: string, resolvedAppSite: string | null) => boolean;
    declare getTemplateDataWithInheritance: (this: LoaderNormalJson, appSite: string, appView: string, appFile: string, dataJson: JsonObject, resolvedTemplateName: string, dataJsonCache: Map<string, Map<string, DataJsonCacheEntry>>, maxRecursionDepth: number, flowStack?: string[] | null, loader?: LoaderNormalJson | null, primaryAppSite?: string | null, resolvedAppSite?: string | null, configJson?: JsonObject | null, htmlJson?: JsonObject | null, clientMode?: boolean) => JsonObject | null;
    declare applyGlobalData: (this: LoaderNormalJson, appSite: string, componentName: string, componentJson: JsonObject, resolvedAppSite: string | null) => void;
    declare getWithFallback: (this: LoaderNormalJson, targetAppSite: string, appSite: string, getter: (site: string) => JsonObject | PlainJsonObject | null) => JsonObject | null;
    declare getGlobalDataWithFallback: (this: LoaderNormalJson, targetAppSite: string, appSite: string) => JsonObject | null;
    declare getGlobalViewDataForComponentWithFallback: (this: LoaderNormalJson, targetAppSite: string, appSite: string, componentName: string) => JsonObject | null;
    declare findKeyIgnoreCase: (this: LoaderNormalJson, data: JsonObject, key: string) => string | null;
    declare getScalarString: (this: LoaderNormalJson, val: JsonValue) => string | null;
    declare matchesByStringKeys: (this: LoaderNormalJson, componentItem: PlainJsonObject | PlainJsonMap, globalItem: PlainJsonObject | PlainJsonMap, primaryKey: string | null) => boolean;
    declare mergeGlobalDataIntoComponent: (this: LoaderNormalJson, componentJson: JsonObject | PlainJsonObject, globalData: JsonObject | PlainJsonObject | null) => void;

    // --- From loaderNormalJson_Dynamic.ts (dynamicMethods) ---
    declare getTemplateWithView: (this: LoaderNormalJson, appSite: string, currentTemplateName: string, componentName: string, appView?: string | null, flowStack?: string[] | null, configJson?: JsonObject | null, clientMode?: boolean) => GetTemplateJsonResult;
    declare tryBuildParentJsonValueHandlerForAction: (this: LoaderNormalJson, appSite: string, flowStack: string[] | null, contextJson?: JsonObject | null) => ((key: string) => string | null) | null;
    declare applyDynamicDataHandlers: (this: LoaderNormalJson, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject | PlainJsonObject, configJson?: JsonObject | null, flowStack?: string[] | null) => void;
    declare applyDynamicClientDataHandlers: (this: LoaderNormalJson, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject, configJson: JsonObject | null, flowStack?: string[] | null) => void;
    declare reconstructComponentHtmlFromJson: (this: LoaderNormalJson, htmlJson: JsonObject | null) => string;
    declare applyClientHandlers: (this: LoaderNormalJson, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, htmlJson: JsonObject | null, resolvedJson: JsonObject, configJson: JsonObject | null, flowStack?: string[] | null) => void;
    declare getClientDataSlice: (this: LoaderNormalJson, appSite: string, handlerName: string, callerParams: JsonObject) => JsonObject | null;
    declare getClientDataIslands: (this: LoaderNormalJson, appSite: string, callerParams: JsonObject) => JsonObject;
    declare hasClientData: (this: LoaderNormalJson, appSite: string) => boolean;
    declare applyDynamicViewHandlersJson: (this: LoaderNormalJson, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtmlJson: JsonObject, componentJson: JsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson?: JsonObject | null) => JsonObject;
    declare _applyDynamicViewHandlersJson: (this: LoaderNormalJson, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtmlJson: JsonObject, componentJson: JsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson?: JsonObject | null) => JsonObject;
    declare applyDynamicActionHandlers: (this: LoaderNormalJson, appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject | PlainJsonObject, configJson?: JsonObject | null, flowStack?: string[] | null) => void;
    declare executeDynamicAction: (this: LoaderNormalJson, appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, actionJson: JsonObject | null, actionHandler: import('../../../../Assembler/dist/registry/iface/iAction.js').IAction, getParentJsonValue: ((key: string) => string | null) | null) => void;
    declare executeWithReadOnly: <T>(this: LoaderNormalJson, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, action: () => T) => T;
    declare syncDynamicActionResults: (this: LoaderNormalJson, componentName: string, afterJson: unknown) => void;
    declare _applyDynamicDataHandlersSync: (this: LoaderNormalJson, appSite: string, componentName: string, componentJson: PlainJsonObject) => void;
    declare _convertJsonArrayToPlain: (this: LoaderNormalJson, jsonArray: import('../../../../Assembler/dist/app/json/jsonValue.js').JsonArray) => unknown[];
    declare _convertJsonObjectToPlain: (this: LoaderNormalJson, jsonObject: JsonObject) => PlainJsonObject;
    declare _unwrapJsonValue: (this: LoaderNormalJson, jsonValue: JsonValue) => unknown;

    // --- Static method declarations (attached via Object.assign) ---
    static _buildAppSitesToLoad: (appSite: string, searchAppSites: string) => string[];

    // #endregion

    resolveReuseDataJson(
        appSite: string,
        reuseJsonName: string,
        fallbackTemplateName: string,
        resolvedAppSite: string
    ): { dataJson: JsonObject | null; templateKey: string; resolvedAppSite: string } {
        return {
            dataJson: this.getDataJson(appSite, reuseJsonName),
            templateKey: fallbackTemplateName,
            resolvedAppSite
        };
    }

    trySearchAppSitesLookup(
        appSite: string,
        baseTemplateName: string,
        reuseJsonName: string | null
    ): { htmlJson: JsonObject | null; dataJson: JsonObject | null; templateKey: string; resolvedAppSite: string } {
        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (!searchAppSites) {
            return { htmlJson: null, dataJson: null, templateKey: '', resolvedAppSite: appSite };
        }

        for (const searchSite of searchAppSites.split(',')) {
            const searchSiteTrimmed = searchSite.trim();
            if (!searchSiteTrimmed) continue;

            const htmlJson = this.getHtmlJson(searchSiteTrimmed, baseTemplateName);
            if (!htmlJson) continue;

            if (reuseJsonName) {
                const reuseData = this.resolveReuseDataJson(appSite, reuseJsonName, reuseJsonName, searchSiteTrimmed);
                return { htmlJson, dataJson: reuseData.dataJson, templateKey: reuseJsonName, resolvedAppSite: searchSiteTrimmed };
            }

            return {
                htmlJson,
                dataJson: this.getDataJson(searchSiteTrimmed, baseTemplateName),
                templateKey: baseTemplateName,
                resolvedAppSite: searchSiteTrimmed
            };
        }

        return { htmlJson: null, dataJson: null, templateKey: '', resolvedAppSite: appSite };
    }

    createDefaultHtmlJson(content: string): JsonObject {
        if (!content) {
            return new JsonObject();
        }

        const meta = {
            hasJsonKeys: false,
            jsonKeys: [] as string[],
            hasPlaceholders: false,
            placeholders: [] as string[],
            hasSlotholders: false,
            slotholders: [] as string[],
            hasArrayBlocks: false,
            arrayBlocks: [] as string[]
        };
        return new JsonObject({ '_meta': meta, 's1': content });
    }

    // #region Constructor

    /**
     * Creates a new loader instance and automatically loads templates
     * @param config - Configuration interface providing file path metadata and SearchAppSites (required)
     * @param appSite - Primary AppSite name to load
     */
    constructor(config: IConfig, appSite: string) {
        super();
        this._config = config;
        this.load(config, appSite);
    }

    // #endregion

    // #region ILoaderNormalJson Interface

    // #region Retrieval Related

    /**
     * Gets a template's HtmlJson and DataJson with AppView and SearchAppSites fallback support.
     */
    getTemplate(appSite: string, currentTemplateName: string, appFileOrPlaceholderOrSlotholderName: string, appView: string | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): GetTemplateJsonResult {
        // === VALIDATION ===
        if (!currentTemplateName || currentTemplateName.trim() === '') {
            throw new Error("currentTemplateName must not be empty - it's required for component reuse pattern detection");
        }

        if (!appFileOrPlaceholderOrSlotholderName || appFileOrPlaceholderOrSlotholderName.trim() === '') {
            if (this._config && typeof this._config.getAppFile === 'function') {
                const result = this._config.getAppFile(appSite, appView || '');
                appFileOrPlaceholderOrSlotholderName = result.appFile;
            } else {
                appFileOrPlaceholderOrSlotholderName = this._config!.defaultAppFile;
            }
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`getTemplate: appSite='${appSite}', currentTemplate='${currentTemplateName}', name='${appFileOrPlaceholderOrSlotholderName}', appView='${appView}'`, 'LoaderNormalJson');
        }

        // === 1. SKIP MARKER CHECK ===
        if (appFileOrPlaceholderOrSlotholderName.startsWith('!')) {
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`getTemplate: Skip marker detected in '${appFileOrPlaceholderOrSlotholderName}', returning skip defaults`, 'LoaderNormalJson');
            }
            const skipHtmlJson = this.createDefaultHtmlJson(this._config!.defaultSkipHtml);
            return { htmlJson: skipHtmlJson, dataJson: null, resolvedTemplateName: this._config!.defaultSkipTemplateName, resolvedAppSite: appSite };
        }

        // === 2. COMPONENT REUSE PATTERN CHECK ===
        let baseTemplateName = appFileOrPlaceholderOrSlotholderName;
        let reuseJsonName: string | null = null;

        const parts = appFileOrPlaceholderOrSlotholderName.split('_');
        if (parts.length >= 3 && currentTemplateName) {
            for (let i = 1; i < parts.length - 1; i++) {
                if (!isNaN(parseInt(parts[i]))) {
                    const prefix = parts.slice(0, i).join('_');
                    if (prefix.toLowerCase() === currentTemplateName.toLowerCase()) {
                        baseTemplateName = parts.slice(i + 1).join('_');
                        reuseJsonName = appFileOrPlaceholderOrSlotholderName;
                        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                            Logger.debug(`getTemplate: Component reuse detected - Original: '${appFileOrPlaceholderOrSlotholderName}', Base: '${baseTemplateName}', JSON: '${reuseJsonName}'`, 'LoaderNormalJson');
                        }
                        break;
                    }
                }
            }
        }

        let htmlJson: JsonObject | null = null;
        let dataJson: JsonObject | null = null;
        let templateKey = '';
        let resolvedAppSite = appSite;

        // === 3. APPVIEW FALLBACK ===
        if (appView && this._config!.defaultAppViewPrefix &&
            baseTemplateName.toLowerCase().includes(this._config!.defaultAppViewPrefix.toLowerCase())) {
            const appViewTemplate = replaceCaseInsensitive(baseTemplateName, this._config!.defaultAppViewPrefix, appView);
            htmlJson = this.getHtmlJson(appSite, appViewTemplate);
            if (htmlJson) {
                if (reuseJsonName) {
                    dataJson = this.getDataJson(appSite, reuseJsonName);
                    templateKey = appFileOrPlaceholderOrSlotholderName;
                } else {
                    dataJson = this.getDataJson(appSite, appViewTemplate);
                    templateKey = appViewTemplate;
                }
                resolvedAppSite = appSite;
            }
        }

        // === 4. PRIMARY APPSITE LOOKUP ===
        if (!htmlJson) {
            htmlJson = this.getHtmlJson(appSite, baseTemplateName);
            if (htmlJson) {
                if (reuseJsonName) {
                    dataJson = this.getDataJson(appSite, reuseJsonName);
                    templateKey = appFileOrPlaceholderOrSlotholderName;
                } else {
                    dataJson = this.getDataJson(appSite, baseTemplateName);
                    templateKey = baseTemplateName;
                }
                resolvedAppSite = appSite;
            }
        }

        // === 5. SEARCHAPPSITES FALLBACK ===
        if (!htmlJson) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                const searchAppSitesArray = searchAppSites.split(',');
                for (const searchSite of searchAppSitesArray) {
                    const searchSiteTrimmed = searchSite.trim();
                    if (!searchSiteTrimmed) continue;

                    htmlJson = this.getHtmlJson(searchSiteTrimmed, baseTemplateName);
                    if (htmlJson) {
                        if (reuseJsonName) {
                            dataJson = this.getDataJson(appSite, reuseJsonName);
                            templateKey = appFileOrPlaceholderOrSlotholderName;
                        } else {
                            dataJson = this.getDataJson(searchSiteTrimmed, baseTemplateName);
                            templateKey = baseTemplateName;
                        }
                        resolvedAppSite = searchSiteTrimmed;
                        break;
                    }
                }
            }
        }

        // === 6. NOT FOUND - RETURN DEFAULT ===
        if (!htmlJson) {
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`getTemplate: Template not found: ${appFileOrPlaceholderOrSlotholderName} (base: ${baseTemplateName}), returning defaults`, 'LoaderNormalJson');
            }
            htmlJson = this.createDefaultHtmlJson(this._config!.defaultReplaceHtml);
            templateKey = this._config!.defaultReplaceTemplateName;
            resolvedAppSite = appSite;
        }

        // === 7. POST-PROCESSING ===
        let resolvedDataJson: JsonObject | null = null;
        if (dataJson && templateKey) {
            const dynamicDataAppSite = this._config!.dynamicDataUseResolvedAppSite ? resolvedAppSite : appSite;
            resolvedDataJson = this._getTemplateDataJsonWithInheritance(
                dynamicDataAppSite,
                appView || '',
                appFileOrPlaceholderOrSlotholderName,
                dataJson,
                templateKey,
                flowStack,
                resolvedAppSite,
                configJson,
                htmlJson,
                clientMode
            );
        }

        return { htmlJson, dataJson: resolvedDataJson, resolvedTemplateName: templateKey, resolvedAppSite };
    }

    // #endregion

    // #region Template Access

    /**
     * Gets HtmlJson structure for a specific template (case-insensitive lookup)
     */
    getHtmlJson(appSite: string, templateName: string): JsonObject | null {
        if (this._htmlJsonCache.has(appSite)) {
            const templates = this._htmlJsonCache.get(appSite)!;
            for (const [key, value] of templates.entries()) {
                if (key.toLowerCase() === templateName.toLowerCase()) {
                    return value.jsonStructure;
                }
            }
        }
        return null;
    }

    /**
     * Gets DataJson structure for a specific template (case-insensitive lookup)
     */
    getDataJson(appSite: string, templateName: string): JsonObject | null {
        if (this._dataJsonCache.has(appSite)) {
            const templates = this._dataJsonCache.get(appSite)!;
            const dataJsonKey = `${appSite.toLowerCase()}_${templateName.toLowerCase()}`;
            if (templates.has(dataJsonKey)) {
                return (templates.get(dataJsonKey)! as DataJsonCacheEntry).jsonStructure;
            }
        }

        return null;
    }

    /**
     * Gets all template names for a specific AppSite
     */
    getAllTemplateNames(appSite: string): string[] {
        const templates: string[] = [];
        if (this._htmlJsonCache.has(appSite)) {
            const appSiteTemplates = this._htmlJsonCache.get(appSite)!;
            for (const key of appSiteTemplates.keys()) {
                templates.push(key);
            }
        }
        return templates;
    }

    // #endregion

    // #region Configuration Properties
    declare readonly defaultReplaceTemplateName: string;
    declare readonly defaultSkipTemplateName: string;
    declare readonly defaultReplaceHtml: string;
    declare readonly defaultSkipHtml: string;
    declare readonly defaultSkipJson: string;
    declare readonly defaultAppViewPrefix: string;
    declare readonly maxArrayOperationItems: number;
    declare readonly maxRecursionDepth: number;
    declare readonly maxMergeIterations: number;

    get defaultAppFile(): string {
        return this._config!.defaultAppFile;
    }

    // skipDynamicView/skipDynamicData property accessors are attached from
    // loaderNormalJson_Accessors.ts (mirrors C# LoaderNormalJson_Accessors.cs partial-class file).
    declare skipDynamicView: string | null;
    declare skipDynamicData: string | null;

    // #endregion

    // #region Private Helpers

    /**
     * Merges DataJson values into HtmlJson structure
     * @private
     */
    _mergeDataJsonIntoHtmlJson(htmlJson: PlainJsonObject, dataJson: PlainJsonObject, templateKey: string = '', currentTemplateName: string = '', operations: TemplateOperation[] | null = null): PlainJsonObject {
        const result: PlainJsonObject = {};

        for (const [key, value] of Object.entries(htmlJson)) {
            const keyType = key.includes('_') ? key.substring(key.indexOf('_') + 1) : key;

            if (keyType.startsWith('JsonKey')) {
                const placeholder = value as string;
                const keyName = placeholder.substring(3, placeholder.length - 2);

                if (dataJson[keyName] !== undefined) {
                    result[key] = this._stringifyJsonValue(dataJson[keyName]);

                    if (operations && templateKey && currentTemplateName) {
                        operations.push({
                            templateName: templateKey,
                            kind: TemplateOperationKind.KeyReplace,
                            target: placeholder,
                            replacedWith: `${currentTemplateName}_${keyName}`,
                            operations: []
                        } as unknown as TemplateOperation);
                    }
                } else {
                    result[key] = value;
                }
            } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                result[key] = this._mergeDataJsonIntoHtmlJson(value as PlainJsonObject, dataJson, templateKey, currentTemplateName, operations);
            } else {
                result[key] = value;
            }
        }

        return result;
    }

    /**
     * Converts a value to string representation
     * @private
     */
    _stringifyJsonValue(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        if (typeof value === 'string') {
            return value;
        }

        if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
        }

        return String(value);
    }

    // #endregion

    // #region Statistics

    static _countJsonKeysAndArrays(json: JsonObject | PlainJsonObject, stats: ResourceStatistics): void {
        if (!json) return;

        const keys: Iterable<string> = json instanceof JsonObject ? json.keys() : Object.keys(json);

        for (const key of keys) {
            stats.jsonKeyCount++;

            let value: unknown = json instanceof JsonObject ? json.get(key) : (json as PlainJsonObject)[key];

            if (value instanceof JsonValue) {
                if (value.isObject) {
                    value = value.asObject();
                } else if (value.isArray) {
                    value = value.asArray();
                } else {
                    continue;
                }
            }

            if (value instanceof JsonObject) {
                LoaderNormalJson._countJsonKeysAndArrays(value, stats);
            } else if (value instanceof JsonArray) {
                stats.jsonArrayCount++;
                for (let i = 0; i < value.length; i++) {
                    let item: unknown = value.get(i);
                    if (item instanceof JsonValue) {
                        if ((item as JsonValue).isObject) {
                            item = (item as JsonValue).asObject();
                        } else {
                            continue;
                        }
                    }
                    if (item instanceof JsonObject) {
                        LoaderNormalJson._countJsonKeysAndArrays(item, stats);
                    }
                }
            } else if (Array.isArray(value)) {
                stats.jsonArrayCount++;
                for (const item of value) {
                    if (item && typeof item === 'object' && !(item instanceof JsonArray)) {
                        LoaderNormalJson._countJsonKeysAndArrays(item as PlainJsonObject, stats);
                    }
                }
            } else if (value && typeof value === 'object' && !(value instanceof JsonArray)) {
                LoaderNormalJson._countJsonKeysAndArrays(value as PlainJsonObject, stats);
            }
        }
    }

    static _countHandlersInConfig(config: JsonObject | PlainJsonObject | DynamicConfigMap): number {
        let count = 0;
        const keys: Iterable<string> = config instanceof JsonObject ? config.keys() : Object.keys(config as PlainJsonObject);

        for (const componentKey of keys) {
            let handlersArray: unknown = config instanceof JsonObject ? config.get(componentKey) : (config as PlainJsonObject)[componentKey];

            if (handlersArray instanceof JsonValue) {
                if ((handlersArray as JsonValue).isArray) {
                    handlersArray = (handlersArray as JsonValue).asArray();
                } else {
                    continue;
                }
            }

            if (handlersArray) {
                if (handlersArray instanceof JsonArray) {
                    count += handlersArray.length;
                } else if (Array.isArray(handlersArray)) {
                    count += handlersArray.length;
                }
            }
        }
        return count;
    }

    // #endregion
}

// Apply ILoader interface methods from split file (matching C# partial class pattern)
// Use defineProperties to properly transfer getters (Object.assign would invoke them)
Object.assign(LoaderNormalJson.prototype, lifecycleMethods);
Object.defineProperties(LoaderNormalJson.prototype, Object.getOwnPropertyDescriptors(iloaderMethods));
Object.assign(LoaderNormalJson.prototype, inheritanceMethods);

// Apply dynamic methods from dynamicMethods
Object.assign(LoaderNormalJson.prototype, dynamicMethods);

// Apply api methods
Object.assign(LoaderNormalJson.prototype, apiMethods);

// Apply accessor methods
// accessorMethods uses Object.defineProperties via Object.getOwnPropertyDescriptors so
// property accessor pairs (get/set skipDynamicView etc.) are preserved on the prototype.
Object.defineProperties(LoaderNormalJson.prototype, Object.getOwnPropertyDescriptors(accessorMethods));

// Apply static methods
Object.assign(LoaderNormalJson, accessorStaticMethods);
